import * as THREE from "three";
import type { RareDef } from "../../rare/rareApi";
import type { WyrmDef } from "../../rare/wyrmApi";
import { hqNode } from "./common";
import { Death, type EnemyView } from "./enemyView";
import { RareTaunt, rareArrive, rareRoar } from "./rareClips";
import { RareShiny } from "./rareShiny";
import { ScreenFlashFn, WyrmDeath, WyrmDeathFx } from "./wyrmDeath";
import { WyrmTrail } from "./wyrmTrail";
import { WYRM_CLIPS } from "./wyrmView";

/**
 * A rare's emissive wash in its sparkle colour (RareDef.wash overrides). Faint: the wash is added light, the same on a
 * pale shell and a dark hide, and at 0.06 it greyed the Ram's navy and the Salamander's soot toward a pale ghost.
 */
export const RARE_WASH = 0.02;

const _feetAt = new THREE.Vector3();
const _centre = new THREE.Vector3();

/** Where the showpiece is drawn this frame (rareMoment.ts, wyrmMoment.ts); `feet` and `centre` are reused vectors. */
export interface ShowpieceDrawn { feet: THREE.Vector3; height: number; alive: boolean; centre: THREE.Vector3; wyrm: boolean }

/**
 * The one showpiece a level has on its road at a time — an M36 rare or the M40 Starfall Wyrm (they never share the
 * field) — for EnemyViews: its shine (rareShiny.ts: the body's sheen and glow pulse, orbiting motes, a sparkle trail),
 * its body washed in its own colours instead of the boss red, and for the Wyrm its comet trail from its tail star and
 * wingtips (wyrmTrail.ts), and the Wyrm's death burst when its fall crashes (wyrmDeath.ts). Each Points layer is built
 * with the first showpiece a level sees and joins `group` once. `fx` and `screenFlash` are read when needed (the level
 * sets them after the views are built).
 */
export class Showpiece {
  private shiny: RareShiny | null = null;
  private trail: WyrmTrail | null = null;
  /** The view being shone (alive, dissolving or fleeing), or null. */
  private view: EnemyView | null = null;
  /** A rare's roar now and then while it walks (rareClips.ts); the Wyrm roars on its own cues (wyrmView.ts). */
  private readonly taunt = new RareTaunt();
  /** The slain Wyrm's cracks and its burst on the crash. */
  private readonly death = new WyrmDeath();

  constructor(private readonly group: THREE.Group, private readonly fx: () => WyrmDeathFx | null = () => null, private readonly screenFlash: () => ScreenFlashFn = () => null) {}

  /** A rare appeared on `v`. */
  begin(v: EnemyView, def: RareDef): void {
    if (!this.shiny) { this.shiny = new RareShiny(); this.group.add(this.shiny.points); }
    this.shiny.begin(v.look, def.sparkA, def.sparkB, def.shine ?? 1);
    // the body's wash in its colour instead of the boss red: it reads as treasure; per creature, its albedo (the Pearl
    // Tortoise's white shell bloomed into a dome) and its `_glow` slot (the Salamander's embers, the Wyrm's stars) — from
    // the material's own values, kept in userData, so a pooled view never compounds them
    v.body.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      for (const m of (Array.isArray(mesh.material) ? mesh.material : [mesh.material]) as (THREE.MeshStandardMaterial & THREE.MeshBasicMaterial)[]) {
        if (!m || !m.color) continue;
        const base = (m.userData.tdRareBase ??= m.color.getHex()) as number;
        const glowSlot = /_glow/i.test(m.name ?? "") || /_glow/i.test(mesh.name);
        m.color.setHex(base).multiplyScalar(glowSlot ? def.glow ?? 1 : def.albedo ?? 1);
        if (m.isMeshStandardMaterial) m.emissive.setHex(def.sparkA).multiplyScalar(def.wash ?? RARE_WASH);
      }
    });
    this.view = v;
    this.taunt.reset();
    if (!v.wyrm) rareArrive(v);                              // a rare bursts up out of the ground at the portal
  }

  /** The Starfall Wyrm appeared on `v`: the rare's shine in its colours, and its comet trail. */
  beginWyrm(v: EnemyView, def: WyrmDef): void {
    this.begin(v, def);
    // the model is the star (M40 review): a soft cyan rim to lift it off the night, no glitter on its body, the motes out
    // past its wingtips and no second trail over it — its own comet trail streams behind
    if (v.look) { v.look.rimColor.setHex(def.sparkC); v.look.rimStrength = 0.4; v.look.rimPulse = 0.12; }
    if (this.shiny) { this.shiny.glitter = 0; this.shiny.orbit = WYRM_CLIPS.moteOrbit; this.shiny.shed = false; }
    if (!this.trail) { this.trail = new WyrmTrail([def.sparkA, def.sparkB, def.sparkC]); this.group.add(this.trail.points); }
    const name = v.model.replace(/^hq:/, "").replace(/_lod1$/, "");
    this.trail.begin(v.body, hqNode(v.body, name, "glow2"), hqNode(v.body, name, "wingtip_l"), hqNode(v.body, name, "wingtip_r"));
  }

  /** Where it is drawn, its drawn height, whether it is alive (`alive(view)`), and the middle of its body — null when none. */
  drawn(alive: (v: EnemyView) => boolean): ShowpieceDrawn | null {
    const v = this.view;
    if (!v) return null;
    const height = (v.hq ? v.headNow : v.headY) || v.height * v.scale;
    if (v.torso) v.torso.getWorldPosition(_centre); else _centre.copy(v.root.position).setY(v.root.position.y + v.body.position.y + height * 0.5);
    return { feet: v.root.position, height, alive: alive(v), centre: _centre, wyrm: v.wyrm };
  }

  /** Per frame: its shine while it flies or walks, and while its body dissolves (`dying`: it is in the dying list). */
  frame(dt: number, reduced: boolean, dying: (v: EnemyView) => boolean): void {
    const v = this.view, s = this.shiny;
    if (v && s && !(v.root.visible && (v.dead <= 0 || dying(v)))) this.end(v);
    const w = this.view;
    if (w && s) {
      const fade = w.dead > 0 ? 1 - (w.look?.dissolve ?? 0) : 1;
      _feetAt.copy(w.root.position); _feetAt.x += w.body.position.x; _feetAt.z += w.body.position.z; _feetAt.y += w.body.position.y;
      s.frame(w.look, _feetAt, (w.hq ? w.headNow : w.headY) || w.height * w.scale, dt, fade, reduced);
      if (w.wyrm) {
        if (w.dead > 0 && w.death === Death.Fall) this.death.frame(w, this.fx(), this.screenFlash(), reduced);
        this.trail?.frame(dt, fade, w.speed > 0.05 || w.dead > 0, reduced);
        return;
      }
      if (this.taunt.tick(dt, w.rig.busy, w.dead <= 0, w.maxHp > 0 ? w.lastHp / w.maxHp : 1)) rareRoar(w);
    }
    this.trail?.frame(dt, 0, false, reduced);                   // no Wyrm: its last sparks fade on their own
  }

  /** `v` went back to the pool: if it was the showpiece, its sprites go. */
  end(v: EnemyView): void {
    if (v !== this.view) return;
    this.death.end(v);
    this.shiny?.end(); this.trail?.end(); this.view = null;
  }

  dispose(): void { this.shiny?.dispose(); this.trail?.dispose(); this.shiny = null; this.trail = null; this.view = null; }
}
